
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, Copy, RefreshCw, Power, Terminal, Zap, Loader2, Lock, Fingerprint, FlaskConical, Globe, Link } from 'lucide-react';
import { SystemConfig } from '../types';
import { toast } from 'react-hot-toast';
import { testAuthConnection } from '../services/openDeliveryService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  config: SystemConfig;
  setConfig: (config: SystemConfig) => void;
}

export const OpenDeliveryModal: React.FC<Props> = ({ isOpen, onClose, config, setConfig }) => {
  const [activeTab, setActiveTab] = useState<'status' | 'credentials' | 'security'>('status');
  const [isTesting, setIsTesting] = useState(false);

  const odConfig = config.openDelivery || {
      active: false,
      mode: 'simulation',
      merchantId: '',
      pollingIntervalSeconds: 30,
      clientId: '',
      clientSecret: '',
      baseUrl: 'https://integracao.cardapioweb.com/api/open_delivery',
      salAppId: ''
  };

  const updateODConfig = (updates: Partial<typeof odConfig>) => {
      setConfig({ ...config, openDelivery: { ...odConfig, ...updates } });
  };

  const handleTestConnection = async () => {
      setIsTesting(true);
      if (odConfig.mode === 'simulation') {
          await new Promise(r => setTimeout(r, 800));
          toast.success("Pipeline de Simulação: Operacional", { icon: '🧪' });
      } else {
          if (!odConfig.clientId || !odConfig.clientSecret) {
              toast.error("Credenciais ausentes para modo Produção.");
              setIsTesting(false);
              return;
          }
          try {
              const success = await testAuthConnection(odConfig);
              if (success) toast.success("Conexão v1.6.0 (Produção) OK!", { icon: '✅' });
              else toast.error("Falha no Handshake OAuth2. Verifique o Client Secret.");
          } catch (e) { toast.error("Erro de rede ao contatar o integrador."); }
      }
      setIsTesting(false);
  };

  const copyToClipboard = (text: string, label: string) => {
      navigator.clipboard.writeText(text);
      toast.success(`${label} copiado!`);
  };

  // Gerador dinâmico da URL de Webhook baseada no host atual
  const currentHost = typeof window !== 'undefined' ? window.location.origin : 'https://sal-engine.run.app';
  const webhookUrl = `${currentHost}/webhook-od`;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-sal-gunmetal/90 backdrop-blur-md" />
        <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="relative bg-sal-gunmetal-light w-full max-w-3xl rounded-3xl shadow-2xl overflow-hidden flex flex-col border border-white/10 max-h-[90vh]">
            <div className="bg-sal-gunmetal px-6 py-6 border-b border-white/5 flex justify-between items-center shrink-0">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-red-600 rounded-2xl shadow-xl shadow-red-900/20 text-white"><Zap className="w-6 h-6" /></div>
                    <div>
                        <h2 className="text-2xl font-black text-white tracking-tight uppercase leading-none">Open Delivery Hub</h2>
                        <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-1">Conformidade Versão 1.6.0</p>
                    </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto no-scrollbar">
                <div className="flex gap-2 mb-8 p-1.5 bg-black/20 rounded-2xl w-fit">
                    {['status', 'credentials', 'security'].map((t) => (
                        <button key={t} onClick={() => setActiveTab(t as any)} className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === t ? 'bg-white/10 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}>
                            {t === 'status' ? 'Canal' : t === 'credentials' ? 'API Key' : 'Segurança'}
                        </button>
                    ))}
                </div>

                {activeTab === 'status' && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                        
                        {/* ENDPOINT WEBHOOK (CAMPO SOLICITADO) */}
                        <div className="bg-sal-gunmetal/60 p-6 rounded-[2rem] border border-sal-cyber-teal/30 shadow-inner relative overflow-hidden group">
                            <div className="absolute top-0 right-0 p-8 opacity-[0.05] pointer-events-none group-hover:rotate-12 transition-transform">
                                <Link className="w-32 h-32 text-sal-cyber-teal" />
                            </div>
                            
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-sal-cyber-teal/10 rounded-xl text-sal-cyber-teal border border-sal-cyber-teal/20">
                                    <Link className="w-4 h-4" />
                                </div>
                                <h4 className="text-xs font-black text-sal-cyber-teal uppercase tracking-[0.2em]">URL do Webhook (Configure no seu PDV)</h4>
                            </div>

                            <div className="relative flex flex-col gap-3">
                                <div className="flex gap-2">
                                    <div className="flex-1 bg-black/60 border border-white/10 rounded-2xl px-5 py-4 font-mono text-base overflow-hidden select-all">
                                        <span className="text-sal-cyber-teal font-bold">{webhookUrl}</span>
                                    </div>
                                    <button 
                                        onClick={() => copyToClipboard(webhookUrl, "URL do Webhook")}
                                        className="bg-sal-cyber-teal text-sal-gunmetal p-4 rounded-2xl hover:brightness-110 active:scale-95 transition-all shadow-lg flex items-center justify-center shrink-0"
                                        title="Copiar URL"
                                    >
                                        <Copy className="w-5 h-5" />
                                    </button>
                                </div>
                                <p className="text-xs text-slate-500 font-bold uppercase tracking-widest ml-1">
                                    Este é o endpoint que seu PDV deve notificar para enviar pedidos em tempo real.
                                </p>
                            </div>
                        </div>

                        {/* AMBIENTE SELECTOR */}
                        <div className="bg-black/20 p-6 rounded-[2rem] border border-white/5">
                            <label className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4 block ml-1">Ambiente de Operação</label>
                            <div className="grid grid-cols-2 gap-3">
                                <button 
                                    onClick={() => updateODConfig({ mode: 'simulation' })}
                                    className={`flex items-center justify-center gap-3 p-4 rounded-2xl border-2 transition-all ${odConfig.mode === 'simulation' ? 'bg-sal-cyber-teal/10 border-sal-cyber-teal text-white' : 'bg-transparent border-white/5 text-slate-500 hover:border-white/10'}`}
                                >
                                    <FlaskConical className={`w-5 h-5 ${odConfig.mode === 'simulation' ? 'text-sal-cyber-teal' : ''}`} />
                                    <div className="text-left">
                                        <span className="block text-xs font-black uppercase tracking-widest">Simulação</span>
                                        <span className="block text-xs opacity-60 uppercase font-bold">Pedidos de Teste</span>
                                    </div>
                                </button>

                                <button 
                                    onClick={() => updateODConfig({ mode: 'real' })}
                                    className={`flex items-center justify-center gap-3 p-4 rounded-2xl border-2 transition-all ${odConfig.mode === 'real' ? 'bg-emerald-500/10 border-emerald-500 text-white' : 'bg-transparent border-white/5 text-slate-500 hover:border-white/10'}`}
                                >
                                    <Globe className={`w-5 h-5 ${odConfig.mode === 'real' ? 'text-emerald-400' : ''}`} />
                                    <div className="text-left">
                                        <span className="block text-xs font-black uppercase tracking-widest">Produção</span>
                                        <span className="block text-xs opacity-60 uppercase font-bold">Ambiente Real</span>
                                    </div>
                                </button>
                            </div>
                        </div>

                        {/* IDENTIDADE SAL */}
                        <div className="bg-gradient-to-br from-sal-gunmetal to-sal-gunmetal-light p-6 rounded-[2rem] border border-white/5 shadow-2xl relative overflow-hidden group">
                            <div className="absolute top-0 right-0 p-8 opacity-[0.03] pointer-events-none group-hover:opacity-[0.05] transition-opacity">
                                <Fingerprint className="w-48 h-48 text-sal-cyber-teal" />
                            </div>
                            
                            <div className="flex items-center gap-3 mb-6">
                                <div className="p-2 bg-sal-cyber-teal/10 rounded-xl text-sal-cyber-teal border border-sal-cyber-teal/20">
                                    <Fingerprint className="w-5 h-5" />
                                </div>
                                <h4 className="text-xs font-black text-sal-cyber-teal uppercase tracking-[0.2em]">Identidade S.A.L. (ID de Integração)</h4>
                            </div>

                            <div className="flex flex-col gap-4 relative z-10">
                                <div className="flex gap-2">
                                    <div className="flex-1 bg-black/40 border border-white/5 rounded-2xl px-6 py-5">
                                        <span className="block text-xs text-slate-500 font-bold uppercase tracking-widest mb-1">Chave Única do Seu Sistema</span>
                                        <span className="text-2xl font-mono font-black text-white tracking-wider">{odConfig.salAppId}</span>
                                    </div>
                                    <button 
                                        onClick={() => copyToClipboard(odConfig.salAppId || '', "ID de Integração")}
                                        className="bg-sal-cyber-teal text-sal-gunmetal p-5 rounded-2xl hover:brightness-110 active:scale-95 transition-all shadow-lg"
                                    >
                                        <Copy className="w-6 h-6" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* STATUS TOGGLE */}
                        <div className={`border-2 rounded-[2rem] p-6 flex items-center justify-between shadow-xl transition-all ${odConfig.active ? 'bg-green-950/10 border-green-500/30' : 'bg-sal-gunmetal border-white/5'}`}>
                            <div className="flex items-center gap-4">
                                <div className={`p-4 rounded-2xl ${odConfig.active ? 'bg-green-500 text-sal-gunmetal shadow-[0_0_20px_rgba(34,197,94,0.4)]' : 'bg-white/5 text-slate-600'}`}><Power className="w-6 h-6" /></div>
                                <div>
                                    <h3 className={`text-xl font-black uppercase tracking-tight ${odConfig.active ? 'text-green-400' : 'text-white'}`}>{odConfig.active ? 'Sincronismo Ativo' : 'Sincronismo Pausado'}</h3>
                                    <p className="text-sm text-slate-500 font-medium">Receptor de pedidos multi-canal habilitado.</p>
                                </div>
                            </div>
                            <button onClick={() => updateODConfig({ active: !odConfig.active })} className={`relative w-14 h-7 rounded-full transition-colors ${odConfig.active ? 'bg-green-500' : 'bg-slate-700'}`}>
                                <div className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform shadow-md ${odConfig.active ? 'translate-x-7' : 'translate-x-0'}`} />
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'credentials' && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                        <div className="bg-sal-gunmetal border border-white/5 rounded-[2rem] p-8 space-y-6">
                            <div>
                                <label className="block text-xs font-black text-slate-600 uppercase tracking-widest mb-2 ml-1">Base URL do Integrador</label>
                                <input type="text" value={odConfig.baseUrl || ''} onChange={(e) => updateODConfig({ baseUrl: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-2xl px-5 py-4 text-base text-white font-mono outline-none" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-xs font-black text-slate-600 uppercase tracking-widest mb-2 ml-1">Client ID</label>
                                    <input type="text" value={odConfig.clientId} onChange={(e) => updateODConfig({ clientId: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-2xl px-5 py-4 text-base text-white font-mono" />
                                </div>
                                <div>
                                    <label className="block text-xs font-black text-slate-600 uppercase tracking-widest mb-2 ml-1">Client Secret</label>
                                    <input type="password" value={odConfig.clientSecret} onChange={(e) => updateODConfig({ clientSecret: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-2xl px-5 py-4 text-base text-white font-mono" />
                                </div>
                            </div>
                        </div>
                        <button onClick={handleTestConnection} disabled={isTesting} className={`w-full py-5 rounded-[1.5rem] font-black text-sm uppercase tracking-[0.2em] border flex items-center justify-center gap-3 transition-all ${odConfig.mode === 'simulation' ? 'bg-sal-cyber-teal/10 text-sal-cyber-teal border-sal-cyber-teal/30' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20'}`}>
                            {isTesting ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />} 
                            Testar Handshake ({odConfig.mode === 'simulation' ? 'SIMULAÇÃO' : 'PRODUÇÃO'})
                        </button>
                    </div>
                )}

                {activeTab === 'security' && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                        <div className="bg-sal-gunmetal border border-white/5 rounded-[2.5rem] p-8 flex flex-col items-center text-center">
                            <div className="w-20 h-20 bg-indigo-500/10 rounded-3xl flex items-center justify-center mb-6 border border-indigo-500/20 text-indigo-400">
                                <Lock className="w-10 h-10" />
                            </div>
                            <h3 className="text-2xl font-black text-white uppercase tracking-tighter mb-2">Seguranca de Inbound</h3>
                            <p className="text-base text-slate-400 leading-relaxed font-medium mb-6">
                                O S.A.L. Rota implementa validação de assinatura **HMAC SHA256**. Certifique-se de que o Integrador está enviando o header `x-app-signature` para evitar rejeição de pedidos.
                            </p>
                            <div className="w-full bg-black/40 p-4 rounded-2xl border border-white/5 text-left">
                                <div className="flex items-center gap-2 mb-2 text-indigo-400">
                                    <Shield className="w-4 h-4" />
                                    <span className="text-xs font-black uppercase tracking-widest">Protocolo v1.6.0 Ativo</span>
                                </div>
                                <code className="text-xs text-slate-500 font-mono break-all">X-App-Signature = HMAC-SHA256(Body, ClientSecret)</code>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="p-6 bg-sal-gunmetal border-t border-white/5 flex justify-end">
                <button onClick={onClose} className="px-12 py-4 bg-white text-sal-gunmetal font-black rounded-2xl shadow-xl hover:bg-slate-200 transition-all text-xs uppercase tracking-[0.2em]">Salvar Configuração</button>
            </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
